import React, { useState, useEffect } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { db } from "../../firebase";
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, getDoc, serverTimestamp, query, where, orderBy, limit,
  writeBatch, increment,
} from "firebase/firestore";
import Modal from "../../components/Modal";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Package, Plus, Pencil, Trash2, AlertTriangle,
  Download, RefreshCw, CheckCircle, ShoppingCart,
  TrendingUp, Search, ArrowUpCircle,
  Filter, BarChart2, DollarSign, Users,
  ClipboardList, Clock, XCircle, Printer,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// StockManager — 5 sub-tabs: Products Catalog, Vendor Stock, Sales History, Stock Movements, Restock Requests
// ─────────────────────────────────────────────────────────────────────────────
function StockManager({ companyId }) {
  const { t } = useLanguage();
  const [tab, setTab] = useState("catalog");

  const TABS = [
    { key: "catalog",   label: t("stock_tab_catalog")   },
    { key: "vendor",    label: t("stock_tab_vendor")     },
    { key: "sales",     label: t("stock_tab_sales")      },
    { key: "movements", label: t("stock_tab_movements")  },
    { key: "requests",  label: t("stock_tab_requests")   },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Package className="w-5 h-5 text-indigo-600" />
          {t("stock_title")}
        </h2>
        <p className="text-gray-500 text-sm mt-1">{t("stock_subtitle")}</p>
      </div>

      {/* Sub-tab bar */}
      <div className="flex gap-2 overflow-x-auto">
        {TABS.map(tab_ => (
          <button key={tab_.key} onClick={() => setTab(tab_.key)}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition whitespace-nowrap ${
              tab === tab_.key
                ? "bg-indigo-600 text-white shadow-sm"
                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
            }`}>
            {tab_.label}
          </button>
        ))}
      </div>

      {tab === "catalog"   && <CatalogTab   companyId={companyId} />}
      {tab === "vendor"    && <VendorStockTab companyId={companyId} />}
      {tab === "sales"     && <SalesHistoryTab companyId={companyId} />}
      {tab === "movements" && <StockMovementsTab companyId={companyId} />}
      {tab === "requests"  && <RestockRequestsTab companyId={companyId} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function formatTs(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
}

function genSKU(name) {
  const prefix = (name || "ITM").replace(/\s+/g, "").toUpperCase().slice(0, 3).padEnd(3, "X");
  return prefix + Math.floor(1000 + Math.random() * 9000);
}

const UNITS = ["piece", "kg", "box", "litre", "pack", "carton"];

function Flash({ msg, isError }) {
  if (!msg) return null;
  return (
    <div className={`rounded-xl p-3 text-sm flex items-center gap-2 ${
      isError ? "bg-red-50 border border-red-200 text-red-700" : "bg-green-50 border border-green-200 text-green-700"
    }`}>
      {isError
        ? <AlertTriangle className="w-4 h-4 shrink-0" />
        : <CheckCircle   className="w-4 h-4 shrink-0" />}
      {msg}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-tab 1 — Products Catalog
// ─────────────────────────────────────────────────────────────────────────────
function CatalogTab({ companyId }) {
  const { t } = useLanguage();
  const [items,       setItems]       = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [vendors,     setVendors]     = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [stockMap,    setStockMap]    = useState({}); // { [itemId]: totalQty }
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");
  const [search,      setSearch]      = useState("");
  const [catFilter,   setCatFilter]   = useState("all");
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget,setDeleteTarget]= useState(null);
  const [deleting,    setDeleting]    = useState(false);

  const emptyForm = {
    name: "", sku: "", unit: "piece", selling_price: "",
    cost_price: "", low_stock_threshold: 5, category: "", active: true,
    quantity: 0,
  };
  const [form, setForm] = useState(emptyForm);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [companyId]);

  async function loadAll() {
    setLoading(true);
    try {
      const [itemsSnap, vendorsSnap] = await Promise.all([
        getDocs(collection(db, "companies", companyId, "stock_catalog")),
        getDocs(query(collection(db, "companies", companyId, "users"), where("role", "==", "vendor"))),
      ]);
      const catalogList = itemsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const vendorList  = vendorsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setItems(catalogList);
      setVendors(vendorList);

      // Aggregate total stock per product across all vendors
      const map = {};
      await Promise.all(vendorList.map(async vendor => {
        const vendorUid = vendor.uid || vendor.id;
        try {
          const stockSnap = await getDocs(
            collection(db, "companies", companyId, "vendor_stock", vendorUid, "items")
          );
          stockSnap.docs.forEach(d => {
            map[d.id] = (map[d.id] || 0) + (d.data().quantity || 0);
          });
        } catch (_) { /* vendor may have no stock yet */ }
      }));
      setStockMap(map);
    } catch (err) {
      setError("Error loading catalog: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setLoading(false);
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...emptyForm });
    setError("");
    setModalOpen(true);
  }

  function openEdit(item) {
    setEditing(item);
    setForm({
      name:                item.name                || "",
      sku:                 item.sku                 || "",
      unit:                item.unit                || "piece",
      selling_price:       item.selling_price       ?? "",
      cost_price:          item.cost_price          ?? "",
      low_stock_threshold: item.low_stock_threshold ?? 5,
      category:            item.category            || "",
      active:              item.active !== false,
      quantity:            item.quantity            ?? 0,
    });
    setError("");
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) return setError(t("stock_name_required"));
    setSaving(true);
    setError("");
    try {
      const data = {
        name:                form.name.trim(),
        sku:                 form.sku.trim() || genSKU(form.name),
        unit:                form.unit,
        selling_price:       parseFloat(form.selling_price)      || 0,
        cost_price:          parseFloat(form.cost_price)         || 0,
        low_stock_threshold: parseInt(form.low_stock_threshold)  || 0,
        category:            form.category.trim(),
        active:              form.active,
        quantity:            parseInt(form.quantity)             || 0,
        updated_at:          serverTimestamp(),
      };
      console.log("Saving product data:", data);
      if (editing) {
        await updateDoc(doc(db, "companies", companyId, "stock_catalog", editing.id), data);
        setItems(prev => prev.map(i => i.id === editing.id ? { ...i, ...data } : i));
        setSuccess(t("stock_product_updated"));
      } else {
        data.created_at = serverTimestamp();
        const ref = await addDoc(collection(db, "companies", companyId, "stock_catalog"), data);
        setItems(prev => [...prev, { id: ref.id, ...data }]);
        setSuccess(t("stock_product_created"));
      }
      setModalOpen(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Error: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setSaving(false);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "companies", companyId, "stock_catalog", deleteTarget.id));
      setItems(prev => prev.filter(i => i.id !== deleteTarget.id));
      setDeleteModal(false);
      setDeleteTarget(null);
    } catch (err) {
      setError("Error deleting: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setDeleting(false);
  }

  async function exportToExcel() {
    try {
      const XLSX = await import("xlsx");
      const rows = items.map(item => ({
        SKU:              item.sku || "—",
        Name:             item.name,
        Category:         item.category || "—",
        Unit:             item.unit,
        "Selling Price":  item.selling_price ?? 0,
        "Cost Price":     item.cost_price    ?? 0,
        "Low Stock At":   item.low_stock_threshold ?? 0,
        "Total In Stock": item.quantity ?? 0,
        Active:           item.active !== false ? "Yes" : "No",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Products");
      XLSX.writeFile(wb, `products-catalog-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      setError("Export error: " + err.message);
    }
  }

  const categories = ["all", ...new Set(items.map(i => i.category).filter(Boolean))];

  const filtered = items.filter(item => {
    const matchSearch = `${item.name} ${item.sku} ${item.category}`.toLowerCase().includes(search.toLowerCase());
    const matchCat    = catFilter === "all" || item.category === catFilter;
    return matchSearch && matchCat;
  });

  function isLow(item) {
    const total = item.quantity ?? 0;
    return total <= (item.low_stock_threshold ?? 0);
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={t("stock_search_products")}
              className="w-full pl-9 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white">
            {categories.map(c => (
              <option key={c} value={c}>{c === "all" ? t("stock_all_categories") : c}</option>
            ))}
          </select>
          <span className="text-sm text-gray-400">{filtered.length} product{filtered.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll}
            className="flex items-center gap-1.5 text-sm text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50">
            <RefreshCw className="w-3.5 h-3.5" /> {t("stock_refresh")}
          </button>
          <button onClick={exportToExcel}
            className="flex items-center gap-1.5 text-sm text-green-700 border border-green-200 bg-green-50 px-3 py-1.5 rounded-lg hover:bg-green-100">
            <Download className="w-3.5 h-3.5" /> {t("stock_export_excel")}
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition">
            <Plus className="w-4 h-4" /> {t("stock_add_product")}
          </button>
        </div>
      </div>

      <Flash msg={success} isError={false} />
      {error && !modalOpen && !deleteModal && <Flash msg={error} isError />}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-400 text-sm">{t("stock_no_products")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">{t("stock_col_product")}</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">{t("stock_col_sku")}</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">{t("stock_col_category")}</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">{t("stock_col_unit")}</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500">{t("stock_col_sell_price")}</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500">{t("stock_col_cost")}</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-gray-500">{t("stock_col_total_stock")}</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-gray-500">{t("stock_col_status")}</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500">{t("stock_col_actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(item => {
                  const total = item.quantity ?? 0;
                  const low   = isLow(item);
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 font-semibold text-gray-800">
                        {item.name}
                        {item.active === false && (
                          <span className="ml-2 text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{t("stock_inactive")}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-500 font-mono text-xs">{item.sku || "—"}</td>
                      <td className="py-3 px-4 text-gray-500">{item.category || "—"}</td>
                      <td className="py-3 px-4 text-gray-500 capitalize">{item.unit}</td>
                      <td className="py-3 px-4 text-right font-medium text-gray-800">
                        {item.selling_price ? item.selling_price.toFixed(2) : "—"}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500">
                        {item.cost_price ? item.cost_price.toFixed(2) : "—"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-bold ${low ? "text-red-600" : "text-gray-800"}`}>{total}</span>
                        {low && (
                          <span className="block text-[10px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded-full mt-0.5">
                            {t("stock_low")}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          item.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                        }`}>
                          {item.active !== false ? t("stock_active") : t("stock_inactive")}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEdit(item)}
                            className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-lg transition">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setDeleteTarget(item); setDeleteModal(true); }}
                            className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <Modal isOpen={true} onClose={() => setModalOpen(false)} title={editing ? t("stock_edit_product") : t("stock_add_product_modal")}>
          <div className="space-y-4">
            {error && <Flash msg={error} isError />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("stock_product_name")} <span className="text-red-400">*</span></label>
                <input type="text" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. Water Bottle" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("stock_sku_label")} <span className="text-xs text-gray-400">{t("stock_sku_hint")}</span></label>
                <input type="text" value={form.sku}
                  onChange={e => setForm(p => ({ ...p, sku: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. WAT001" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("stock_category")}</label>
                <input type="text" value={form.category}
                  onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g. Beverages" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("stock_unit")}</label>
                <select value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("stock_initial_qty")}</label>
                <input type="number" min="0" value={form.quantity}
                  onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("stock_low_threshold")}</label>
                <input type="number" min="0" value={form.low_stock_threshold}
                  onChange={e => setForm(p => ({ ...p, low_stock_threshold: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("stock_sell_price")}</label>
                <input type="number" min="0" step="0.01" value={form.selling_price}
                  onChange={e => setForm(p => ({ ...p, selling_price: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0.00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("stock_cost_price")}</label>
                <input type="number" min="0" step="0.01" value={form.cost_price}
                  onChange={e => setForm(p => ({ ...p, cost_price: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="0.00" />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="active-check" checked={form.active}
                  onChange={e => setForm(p => ({ ...p, active: e.target.checked }))}
                  className="w-4 h-4 rounded accent-indigo-600" />
                <label htmlFor="active-check" className="text-sm font-medium text-gray-700">{t("stock_active_visible")}</label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setModalOpen(false)}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-3 rounded-xl hover:bg-gray-50">
                {t("stock_cancel")}
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-bold py-3 rounded-xl transition">
                {saving ? t("stock_saving") : editing ? t("stock_save_changes") : t("stock_create_product")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <Modal isOpen={true} onClose={() => setDeleteModal(false)} title={t("stock_delete_product")} size="sm">
          <div className="space-y-4 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6 text-red-500" />
            </div>
            <p className="text-sm text-gray-500">
              <strong>{deleteTarget?.name}</strong> — {t("stock_delete_confirm")}
            </p>
            {error && <Flash msg={error} isError />}
            <div className="flex gap-3">
              <button onClick={() => setDeleteModal(false)}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-3 rounded-xl hover:bg-gray-50">
                {t("stock_cancel")}
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-bold py-3 rounded-xl transition">
                {deleting ? t("stock_deleting") : t("yes_delete")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-tab 2 — Vendor Stock (two-panel layout)
// ─────────────────────────────────────────────────────────────────────────────
function VendorStockTab({ companyId }) {
  const { t } = useLanguage();
  const [vendors,         setVendors]         = useState([]);
  const [catalog,         setCatalog]         = useState([]);
  const [stockMap,        setStockMap]        = useState({}); // { [vendorUid]: { [itemId]: qty } }
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState("");
  const [success,         setSuccess]         = useState("");
  const [selectedVendor,  setSelectedVendor]  = useState(null);
  const [productSearch,   setProductSearch]   = useState("");
  const [visibleCount,    setVisibleCount]    = useState(50);

  // Single restock modal
  const [restockModal,    setRestockModal]    = useState(false);
  const [restockItem,     setRestockItem]     = useState(null);
  const [restockQty,      setRestockQty]      = useState("");
  const [restockNote,     setRestockNote]     = useState("");
  const [restocking,      setRestocking]      = useState(false);

  // Restock All modal
  const [restockAllModal,  setRestockAllModal]  = useState(false);
  const [restockAllQtys,   setRestockAllQtys]   = useState({});
  const [restockAllNote,   setRestockAllNote]   = useState("");
  const [restockingAll,    setRestockingAll]    = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [companyId]);

  async function loadAll() {
    setLoading(true);
    try {
      const [usersSnap, catalogSnap] = await Promise.all([
        getDocs(query(collection(db, "companies", companyId, "users"), where("role", "==", "vendor"))),
        getDocs(collection(db, "companies", companyId, "stock_catalog")),
      ]);
      const vendorList  = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const catalogList = catalogSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setVendors(vendorList);
      setCatalog(catalogList);

      const map = {};
      await Promise.all(vendorList.map(async vendor => {
        const vendorUid = vendor.uid || vendor.id;
        try {
          const stockSnap = await getDocs(
            collection(db, "companies", companyId, "vendor_stock", vendorUid, "items")
          );
          map[vendorUid] = {};
          stockSnap.docs.forEach(d => { map[vendorUid][d.id] = d.data().quantity || 0; });
        } catch (_) { map[vendorUid] = {}; }
      }));
      setStockMap(map);
    } catch (err) {
      setError("Error loading stock: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setLoading(false);
  }

  function getVendorUid(vendor) { return vendor.uid || vendor.id; }

  function getQty(vendorUid, itemId) {
    return stockMap[vendorUid]?.[itemId] ?? 0;
  }

  function isLow(vendorUid, itemId) {
    const qty       = stockMap[vendorUid]?.[itemId];
    const threshold = catalog.find(c => c.id === itemId)?.low_stock_threshold ?? 0;
    return qty != null && threshold > 0 && qty <= threshold;
  }

  function getVendorSummary(vendor) {
    const uid      = getVendorUid(vendor);
    const vStock   = stockMap[uid] || {};
    const inStock  = Object.values(vStock).filter(q => q > 0).length;
    const lowCount = catalog.filter(item => isLow(uid, item.id)).length;
    return { inStock, lowCount };
  }

  const totalLowStock = vendors.reduce((sum, v) => sum + getVendorSummary(v).lowCount, 0);
  const selectedUid   = selectedVendor ? getVendorUid(selectedVendor) : null;

  const filteredProducts = catalog.filter(item =>
    !productSearch.trim() || item.name.toLowerCase().includes(productSearch.toLowerCase())
  );
  const visibleProducts = filteredProducts.slice(0, visibleCount);

  function openRestock(item) {
    setRestockItem(item);
    setRestockQty("");
    setRestockNote("");
    setError("");
    setRestockModal(true);
  }

  async function handleRestock() {
    const qty = parseInt(restockQty, 10);
    if (!qty || qty <= 0) return setError("Enter a valid quantity.");
    if (!selectedVendor) return;
    setRestocking(true);
    setError("");
    try {
      const vendorUid   = getVendorUid(selectedVendor);
      const vendorName  = `${selectedVendor.first_name || ""} ${selectedVendor.last_name || ""}`.trim();

      // Read current warehouse stock to validate available quantity
      const catalogRef  = doc(db, "companies", companyId, "stock_catalog", restockItem.id);
      const catalogSnap = await getDoc(catalogRef);
      const warehouseQty = catalogSnap.exists() ? (catalogSnap.data().quantity || 0) : 0;
      if (qty > warehouseQty) {
        setError(`Not enough stock in warehouse. Available: ${warehouseQty} units.`);
        setRestocking(false);
        return;
      }

      const stockRef = doc(db, "companies", companyId, "vendor_stock", vendorUid, "items", restockItem.id);
      const movRef   = doc(collection(db, "companies", companyId, "stock_movements"));

      const batch = writeBatch(db);
      // Deduct from warehouse catalog
      batch.update(catalogRef, { quantity: increment(-qty), updated_at: serverTimestamp() });
      // Add to vendor stock — merge:true creates the doc if it doesn't exist
      batch.set(stockRef, {
        name:                restockItem.name,
        unit:                restockItem.unit || "",
        quantity:            increment(qty),
        low_stock_threshold: restockItem.low_stock_threshold || 0,
        updated_at:          serverTimestamp(),
      }, { merge: true });
      batch.set(movRef, {
        type:         "restock",
        vendor_id:    vendorUid,
        vendor_name:  vendorName,
        product_id:   restockItem.id,
        product_name: restockItem.name,
        quantity:     qty,
        note:         restockNote.trim() || "",
        created_at:   serverTimestamp(),
      });
      await batch.commit();

      // Optimistic local update — vendor stock map
      setStockMap(prev => ({
        ...prev,
        [vendorUid]: { ...(prev[vendorUid] || {}), [restockItem.id]: (prev[vendorUid]?.[restockItem.id] || 0) + qty },
      }));
      // Optimistic local update — catalog quantity
      setCatalog(prev => prev.map(item =>
        item.id === restockItem.id ? { ...item, quantity: (item.quantity || 0) - qty } : item
      ));
      setSuccess(`Restocked "${restockItem.name}" for ${vendorName} (+${qty})`);
      setRestockModal(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Error: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setRestocking(false);
  }

  function openRestockAll() {
    const qtys = {};
    catalog.forEach(item => { qtys[item.id] = ""; });
    setRestockAllQtys(qtys);
    setRestockAllNote("");
    setError("");
    setRestockAllModal(true);
  }

  async function handleRestockAll() {
    const items = catalog.filter(item => parseInt(restockAllQtys[item.id], 10) > 0);
    if (items.length === 0) return setError("Enter a quantity for at least one product.");
    if (!selectedVendor) return;
    setRestockingAll(true);
    setError("");
    try {
      const vendorUid  = getVendorUid(selectedVendor);
      const vendorName = `${selectedVendor.first_name || ""} ${selectedVendor.last_name || ""}`.trim();

      // Read current warehouse quantities for all items being restocked
      const catalogSnaps = await Promise.all(
        items.map(item => getDoc(doc(db, "companies", companyId, "stock_catalog", item.id)))
      );
      // Validate each item has enough warehouse stock
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const qty  = parseInt(restockAllQtys[item.id], 10);
        const warehouseQty = catalogSnaps[i].exists() ? (catalogSnaps[i].data().quantity || 0) : 0;
        if (qty > warehouseQty) {
          setError(`Not enough stock in warehouse for "${item.name}". Available: ${warehouseQty} units.`);
          setRestockingAll(false);
          return;
        }
      }

      const batch = writeBatch(db);

      items.forEach((item, i) => {
        const qty        = parseInt(restockAllQtys[item.id], 10);
        const catalogRef = doc(db, "companies", companyId, "stock_catalog", item.id);
        const stockRef   = doc(db, "companies", companyId, "vendor_stock", vendorUid, "items", item.id);
        const movRef     = doc(collection(db, "companies", companyId, "stock_movements"));
        // Deduct from warehouse
        batch.update(catalogRef, { quantity: increment(-qty), updated_at: serverTimestamp() });
        // Add to vendor stock
        batch.set(stockRef, {
          name:                item.name,
          unit:                item.unit || "",
          quantity:            increment(qty),
          low_stock_threshold: item.low_stock_threshold || 0,
          updated_at:          serverTimestamp(),
        }, { merge: true });
        batch.set(movRef, {
          type:         "restock",
          vendor_id:    vendorUid,
          vendor_name:  vendorName,
          product_id:   item.id,
          product_name: item.name,
          quantity:     qty,
          note:         restockAllNote.trim() || "",
          created_at:   serverTimestamp(),
        });
      });
      await batch.commit();

      // Optimistic local update — vendor stock map
      setStockMap(prev => {
        const updated = { ...(prev[vendorUid] || {}) };
        items.forEach(item => {
          const qty = parseInt(restockAllQtys[item.id], 10);
          updated[item.id] = (updated[item.id] || 0) + qty;
        });
        return { ...prev, [vendorUid]: updated };
      });
      // Optimistic local update — catalog quantities
      setCatalog(prev => prev.map(item => {
        const qty = parseInt(restockAllQtys[item.id], 10);
        return qty > 0 ? { ...item, quantity: (item.quantity || 0) - qty } : item;
      }));
      setSuccess(`Bulk restock complete for ${vendorName} — ${items.length} product${items.length !== 1 ? "s" : ""} updated`);
      setRestockAllModal(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Error: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setRestockingAll(false);
  }

  async function exportToExcel() {
    try {
      const XLSX = await import("xlsx");
      const rows = [];
      vendors.forEach(vendor => {
        const vendorUid = getVendorUid(vendor);
        catalog.forEach(item => {
          rows.push({
            Vendor:       `${vendor.first_name || ""} ${vendor.last_name || ""}`.trim(),
            Email:        vendor.email || "",
            Product:      item.name,
            Unit:         item.unit,
            Quantity:     stockMap[vendorUid]?.[item.id] ?? 0,
            "Low Stock?": isLow(vendorUid, item.id) ? "Yes" : "No",
          });
        });
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Vendor Stock");
      XLSX.writeFile(wb, `vendor-stock-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      setError("Export error: " + err.message);
    }
  }

  return (
    <div className="space-y-4">

      {/* ── Summary bar ── */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-1.5 text-sm text-gray-600">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="font-bold text-gray-800">{vendors.length}</span> vendor{vendors.length !== 1 ? "s" : ""}
          </span>
          <span className="flex items-center gap-1.5 text-sm text-gray-600">
            <Package className="w-4 h-4 text-gray-400" />
            <span className="font-bold text-gray-800">{catalog.length}</span> product{catalog.length !== 1 ? "s" : ""}
          </span>
          {totalLowStock > 0 && (
            <span className="flex items-center gap-1.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 px-3 py-1 rounded-full">
              <AlertTriangle className="w-3.5 h-3.5" />
              {totalLowStock} low stock warning{totalLowStock !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll} disabled={loading}
            className="flex items-center gap-1.5 text-sm text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={exportToExcel}
            className="flex items-center gap-1.5 text-sm text-green-700 border border-green-200 bg-green-50 px-3 py-1.5 rounded-lg hover:bg-green-100">
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
        </div>
      </div>

      <Flash msg={success} isError={false} />
      {error && !restockModal && !restockAllModal && <Flash msg={error} isError />}

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
        </div>
      ) : vendors.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400 text-sm">No vendors found.</div>
      ) : catalog.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400 text-sm">
          No products yet — add products in the Products Catalog tab first.
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4 items-start">

          {/* ── LEFT PANEL — vendor list ── */}
          <div className="w-full lg:w-60 shrink-0">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-700">Select Vendor</p>
              </div>
              <div className="divide-y divide-gray-50 max-h-[500px] overflow-y-auto">
                {vendors.map(vendor => {
                  const isSelected       = selectedVendor?.id === vendor.id;
                  const { inStock, lowCount } = getVendorSummary(vendor);
                  return (
                    <button key={vendor.id}
                      onClick={() => { setSelectedVendor(vendor); setProductSearch(""); setVisibleCount(50); }}
                      className={`w-full text-left px-4 py-3 transition border-l-4 ${
                        isSelected
                          ? "bg-indigo-50 border-indigo-500"
                          : "hover:bg-gray-50 border-transparent"
                      }`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {vendor.first_name} {vendor.last_name}
                        </p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                          vendor.active !== false ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                        }`}>
                          {vendor.active !== false ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {inStock} in stock
                        {lowCount > 0 && <span className="text-red-500 font-semibold"> · {lowCount} low</span>}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── RIGHT PANEL — selected vendor's stock ── */}
          <div className="flex-1 min-w-0">
            {!selectedVendor ? (
              <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">
                <Package className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Select a vendor from the left to view their stock</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">

                {/* Right panel header */}
                <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-bold text-gray-800">
                    {selectedVendor.first_name} {selectedVendor.last_name}'s Stock
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                      <input
                        type="text"
                        value={productSearch}
                        onChange={e => { setProductSearch(e.target.value); setVisibleCount(50); }}
                        placeholder="Search products..."
                        className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 w-44"
                      />
                    </div>
                    <button onClick={openRestockAll}
                      className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition whitespace-nowrap">
                      <Plus className="w-3.5 h-3.5" /> Restock All
                    </button>
                  </div>
                </div>

                {/* Product rows */}
                {visibleProducts.length === 0 ? (
                  <div className="p-8 text-center text-gray-400 text-sm">No products match your search.</div>
                ) : (
                  <div className="divide-y divide-gray-50">
                    {visibleProducts.map(item => {
                      const qty = getQty(selectedUid, item.id);
                      const low = isLow(selectedUid, item.id);
                      return (
                        <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold text-gray-800">{item.name}</p>
                              {item.category && (
                                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{item.category}</span>
                              )}
                              {low && (
                                <span className="flex items-center gap-0.5 text-[10px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded-full">
                                  <AlertTriangle className="w-2.5 h-2.5" /> Low
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">{item.unit}{item.sku ? ` · ${item.sku}` : ""}</p>
                          </div>
                          <div className="text-right shrink-0 mr-2">
                            <p className={`text-lg font-bold leading-none ${low ? "text-red-600" : "text-gray-800"}`}>{qty}</p>
                            <p className="text-[10px] text-gray-400">{item.unit}</p>
                          </div>
                          <button onClick={() => openRestock(item)}
                            className="flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-3 py-2 rounded-lg hover:bg-indigo-100 transition shrink-0">
                            <Plus className="w-3.5 h-3.5" /> Restock
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Load more */}
                {filteredProducts.length > visibleCount && (
                  <div className="px-4 py-3 border-t border-gray-100 text-center">
                    <button onClick={() => setVisibleCount(c => c + 50)}
                      className="text-sm text-indigo-600 font-medium hover:underline">
                      Load more ({filteredProducts.length - visibleCount} remaining)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Single Restock Modal ── */}
      {restockModal && restockItem && selectedVendor && (
        <Modal isOpen={true} onClose={() => setRestockModal(false)} title="Restock Product" size="sm">
          <div className="space-y-4">
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-sm text-gray-700 space-y-1">
              <p>Vendor: <strong>{selectedVendor.first_name} {selectedVendor.last_name}</strong></p>
              <p>Product: <strong>{restockItem.name}</strong></p>
              <p>Current stock: <strong>{getQty(selectedUid, restockItem.id)}</strong> {restockItem.unit}</p>
            </div>
            {error && <Flash msg={error} isError />}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity to Add <span className="text-red-400">*</span></label>
              <input type="number" min="1" value={restockQty}
                onChange={e => setRestockQty(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g. 50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note <span className="text-xs text-gray-400">(optional)</span></label>
              <input type="text" value={restockNote}
                onChange={e => setRestockNote(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder={`e.g. Loaded truck ${new Date().toLocaleDateString()}`} />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setRestockModal(false)} disabled={restocking}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-3 rounded-xl hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleRestock} disabled={restocking}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-bold py-3 rounded-xl transition">
                {restocking ? "Saving..." : "Add Stock"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Restock All Modal ── */}
      {restockAllModal && selectedVendor && (
        <Modal isOpen={true} onClose={() => setRestockAllModal(false)}
          title={`Restock All — ${selectedVendor.first_name} ${selectedVendor.last_name}`} size="md">
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Enter quantities to add. Leave blank to skip a product.</p>
            {error && <Flash msg={error} isError />}
            <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
              {catalog.map(item => (
                <div key={item.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{item.name}</p>
                    <p className="text-xs text-gray-400">Current: {getQty(selectedUid, item.id)} {item.unit}</p>
                  </div>
                  <input
                    type="number" min="1"
                    value={restockAllQtys[item.id] || ""}
                    onChange={e => setRestockAllQtys(prev => ({ ...prev, [item.id]: e.target.value }))}
                    placeholder="Qty"
                    className="w-20 border border-gray-200 rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 shrink-0" />
                </div>
              ))}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note <span className="text-xs text-gray-400">(optional, applies to all)</span></label>
              <input type="text" value={restockAllNote}
                onChange={e => setRestockAllNote(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder={`e.g. Loaded truck ${new Date().toLocaleDateString()}`} />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setRestockAllModal(false)} disabled={restockingAll}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-3 rounded-xl hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleRestockAll} disabled={restockingAll}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-bold py-3 rounded-xl transition">
                {restockingAll ? "Saving..." : "Confirm Restock"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-tab 3 — Sales History
// ─────────────────────────────────────────────────────────────────────────────
function SalesHistoryTab({ companyId }) {
  const [sales,      setSales]      = useState([]);
  const [vendors,    setVendors]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [expanded,   setExpanded]   = useState(null);
  const [search,     setSearch]     = useState("");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [dateFrom,   setDateFrom]   = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [companyId]);

  async function loadAll() {
    setLoading(true);
    try {
      const [salesSnap, vendorsSnap] = await Promise.all([
        getDocs(query(
          collection(db, "companies", companyId, "sales"),
          orderBy("created_at", "desc"),
          limit(500)
        )),
        getDocs(query(collection(db, "companies", companyId, "users"), where("role", "==", "vendor"))),
      ]);
      setSales(salesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setVendors(vendorsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError("Error loading sales: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setLoading(false);
  }

  const filtered = sales.filter(s => {
    const d = s.created_at?.toDate ? s.created_at.toDate() : (s.created_at ? new Date(s.created_at) : null);
    const from = new Date(dateFrom + "T00:00:00");
    const to   = new Date(dateTo   + "T23:59:59");
    if (d && (d < from || d > to)) return false;
    if (vendorFilter !== "all" && s.vendor_id !== vendorFilter) return false;
    const q = search.toLowerCase();
    if (q) {
      const text = `${s.vendor_name} ${s.client_name} ${(s.items || []).map(i => i.product_name || i.item_name).join(" ")}`.toLowerCase();
      if (!text.includes(q)) return false;
    }
    return true;
  });

  const totalRevenue = filtered.reduce((sum, s) => sum + (s.total_amount || 0), 0);

  async function exportToExcel() {
    try {
      const XLSX = await import("xlsx");
      const rows = filtered.map(s => ({
        Date:     formatTs(s.created_at),
        Vendor:   s.vendor_name || "—",
        Client:   s.client_name || "—",
        Products: (s.items || []).map(i => `${i.product_name || i.item_name} x${i.quantity}`).join(", "),
        Total:    (s.total_amount || 0).toFixed(2),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sales");
      XLSX.writeFile(wb, `sales-${dateFrom}-to-${dateTo}.xlsx`);
    } catch (err) {
      setError("Export error: " + err.message);
    }
  }

  return (
    <div className="space-y-4">
      {/* Revenue summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-green-600" />
            <span className="text-xs text-gray-500 font-medium">Total Revenue</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{totalRevenue.toFixed(2)}</p>
          <p className="text-xs text-gray-400 mt-0.5">In selected period</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart className="w-4 h-4 text-indigo-600" />
            <span className="text-xs text-gray-500 font-medium">Total Sales</span>
          </div>
          <p className="text-2xl font-bold text-indigo-700">{filtered.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">Transactions</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <span className="text-xs text-gray-500 font-medium">Avg. Sale</span>
          </div>
          <p className="text-2xl font-bold text-blue-700">
            {filtered.length > 0 ? (totalRevenue / filtered.length).toFixed(2) : "0.00"}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">Per transaction</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search vendor, client, product..."
            className="w-full pl-9 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
        </div>
        <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none bg-white">
          <option value="all">All Vendors</option>
          {vendors.map(v => (
            <option key={v.id} value={v.uid || v.id}>{v.first_name} {v.last_name}</option>
          ))}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
        <span className="text-gray-400 text-sm">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
        <button onClick={loadAll}
          className="flex items-center gap-1.5 text-sm text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <button onClick={exportToExcel}
          className="flex items-center gap-1.5 text-sm text-green-700 border border-green-200 bg-green-50 px-3 py-1.5 rounded-lg hover:bg-green-100">
          <Download className="w-3.5 h-3.5" /> Excel
        </button>
      </div>

      {error && <Flash msg={error} isError />}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <ShoppingCart className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-400 text-sm">No sales found for this period</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Date</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Vendor</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Client</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Products</th>
                <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500">Total</th>
                <th className="py-3 px-4 text-center text-xs font-semibold text-gray-500">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(sale => (
                <React.Fragment key={sale.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-500 text-xs whitespace-nowrap">{formatTs(sale.created_at)}</td>
                    <td className="py-3 px-4 text-gray-700 font-medium">{sale.vendor_name || "—"}</td>
                    <td className="py-3 px-4 text-gray-700">{sale.client_name || "—"}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {(sale.items || []).slice(0, 3).map((it, i) => (
                          <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
                            {it.product_name || it.item_name} ×{it.quantity}
                          </span>
                        ))}
                        {(sale.items || []).length > 3 && (
                          <span className="text-xs text-gray-400">+{sale.items.length - 3} more</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-gray-800">
                      {(sale.total_amount || 0).toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => setExpanded(expanded === sale.id ? null : sale.id)}
                        className="text-xs text-indigo-600 hover:underline">
                        {expanded === sale.id ? "Hide" : "View"}
                      </button>
                    </td>
                  </tr>
                  {expanded === sale.id && (
                    <tr className="bg-indigo-50">
                      <td colSpan={6} className="px-6 py-4">
                        <p className="text-xs font-semibold text-gray-500 mb-2">
                          Receipt #{sale.id.slice(-6).toUpperCase()} · {formatTs(sale.created_at)}
                        </p>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-500">
                              <th className="text-left py-1">Product</th>
                              <th className="text-center py-1">Qty</th>
                              <th className="text-right py-1">Unit Price</th>
                              <th className="text-right py-1">Subtotal</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(sale.items || []).map((it, i) => (
                              <tr key={i} className="border-t border-indigo-100">
                                <td className="py-1 font-medium text-gray-800">{it.product_name || it.item_name}</td>
                                <td className="py-1 text-center text-gray-600">{it.quantity} {it.unit}</td>
                                <td className="py-1 text-right text-gray-600">{(it.selling_price || 0).toFixed(2)}</td>
                                <td className="py-1 text-right font-semibold text-gray-800">{(it.subtotal || (it.quantity * (it.selling_price || 0))).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-indigo-200">
                              <td colSpan={3} className="py-1.5 text-right font-bold text-gray-700">Total</td>
                              <td className="py-1.5 text-right font-bold text-gray-900">{(sale.total_amount || 0).toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-tab 4 — Stock Movements
// ─────────────────────────────────────────────────────────────────────────────
function StockMovementsTab({ companyId }) {
  const [movements,    setMovements]    = useState([]);
  const [vendors,      setVendors]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [typeFilter,   setTypeFilter]   = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [search,       setSearch]       = useState("");
  const [dateFrom,     setDateFrom]     = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [companyId]);

  async function loadAll() {
    setLoading(true);
    try {
      const [movSnap, vendorsSnap] = await Promise.all([
        getDocs(query(
          collection(db, "companies", companyId, "stock_movements"),
          orderBy("created_at", "desc"),
          limit(500)
        )),
        getDocs(query(collection(db, "companies", companyId, "users"), where("role", "==", "vendor"))),
      ]);
      setMovements(movSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setVendors(vendorsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError("Error loading movements: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setLoading(false);
  }

  const filtered = movements.filter(m => {
    const d = m.created_at?.toDate ? m.created_at.toDate() : (m.created_at ? new Date(m.created_at) : null);
    const from = new Date(dateFrom + "T00:00:00");
    const to   = new Date(dateTo   + "T23:59:59");
    if (d && (d < from || d > to)) return false;
    if (typeFilter !== "all" && m.type !== typeFilter) return false;
    if (vendorFilter !== "all" && m.vendor_id !== vendorFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const text = `${m.vendor_name} ${m.product_name} ${m.note || ""}`.toLowerCase();
      if (!text.includes(q)) return false;
    }
    return true;
  });

  const typeColors = {
    restock:    "bg-green-100 text-green-700",
    sale:       "bg-indigo-100 text-indigo-700",
    adjustment: "bg-yellow-100 text-yellow-700",
  };
  const typeIcons = {
    restock:    ArrowUpCircle,
    sale:       ShoppingCart,
    adjustment: BarChart2,
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full pl-9 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none bg-white">
          <option value="all">All Types</option>
          <option value="restock">Restock</option>
          <option value="sale">Sale</option>
          <option value="adjustment">Adjustment</option>
        </select>
        <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none bg-white">
          <option value="all">All Vendors</option>
          {vendors.map(v => (
            <option key={v.id} value={v.uid || v.id}>{v.first_name} {v.last_name}</option>
          ))}
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
        <span className="text-gray-400 text-sm">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
        <button onClick={loadAll}
          className="flex items-center gap-1.5 text-sm text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <span className="text-sm text-gray-400">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {error && <Flash msg={error} isError />}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Filter className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-400 text-sm">No movements found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Date</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Type</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Vendor</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Product</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-gray-500">Qty Change</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(mov => {
                  const TypeIcon = typeIcons[mov.type] || BarChart2;
                  const isPositive = mov.quantity > 0;
                  return (
                    <tr key={mov.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-500 text-xs whitespace-nowrap">{formatDate(mov.created_at)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium capitalize ${typeColors[mov.type] || "bg-gray-100 text-gray-600"}`}>
                          <TypeIcon className="w-3 h-3" />
                          {mov.type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-700 text-xs">{mov.vendor_name || "—"}</td>
                      <td className="py-3 px-4 text-gray-700 font-medium text-xs">{mov.product_name || "—"}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`font-bold ${isPositive ? "text-green-600" : "text-red-600"}`}>
                          {isPositive ? "+" : ""}{mov.quantity}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{mov.note || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-tab 5 — Restock Requests
// ─────────────────────────────────────────────────────────────────────────────
function RestockRequestsTab({ companyId }) {
  const [requests,      setRequests]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [success,       setSuccess]       = useState("");
  const [statusFilter,  setStatusFilter]  = useState("pending");
  const [expanded,      setExpanded]      = useState(null);
  const [editModal,     setEditModal]     = useState(false);
  const [editTarget,    setEditTarget]    = useState(null);
  const [editItems,     setEditItems]     = useState([]);   // [{ item_id, item_name, item_unit, requested_qty: string }]
  const [editNote,      setEditNote]      = useState("");
  const [saving,        setSaving]        = useState(false);
  const [approvingId,   setApprovingId]   = useState(null);
  const [rejectingId,   setRejectingId]   = useState(null);

  // Normalize old single-item format and new multi-item format
  function getReqItems(req) {
    if (req.items && req.items.length > 0) return req.items;
    if (req.item_id) return [{ item_id: req.item_id, item_name: req.item_name, item_unit: req.item_unit, requested_qty: req.requested_qty }];
    return [];
  }

  // Branding for PDF (same as ReportsPage)
  const [primaryColor,  setPrimaryColor]  = useState("#1a56a0");
  const [companyName,   setCompanyName]   = useState("");
  const [companyLogo,   setCompanyLogo]   = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [companyId]);

  async function loadAll() {
    setLoading(true);
    try {
      const [reqSnap, profileSnap, rootSnap] = await Promise.all([
        getDocs(query(
          collection(db, "companies", companyId, "restock_requests"),
          orderBy("created_at", "desc"),
          limit(200)
        )),
        getDoc(doc(db, "companies", companyId, "profile", "info")),
        getDoc(doc(db, "companies", companyId)),
      ]);
      setRequests(reqSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      if (profileSnap.exists()) {
        const pd = profileSnap.data();
        if (pd.primary_color) setPrimaryColor(pd.primary_color);
        if (pd.logo_url)      setCompanyLogo(pd.logo_url);
      }
      if (rootSnap.exists()) {
        const rd = rootSnap.data();
        if (rd.name) setCompanyName(rd.name);
        if (rd.logo_url && !companyLogo) setCompanyLogo(rd.logo_url);
      }
    } catch (err) {
      setError("Error loading requests: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setLoading(false);
  }

  function openEdit(req) {
    setEditTarget(req);
    setEditItems(getReqItems(req).map(it => ({ ...it, requested_qty: String(it.requested_qty || "") })));
    setEditNote(req.note || "");
    setError("");
    setEditModal(true);
  }

  async function handleSaveEdit() {
    const validItems = editItems
      .filter(it => parseInt(it.requested_qty, 10) > 0)
      .map(it => ({ ...it, requested_qty: parseInt(it.requested_qty, 10) }));
    if (validItems.length === 0) return setError("Enter at least one valid quantity.");
    setSaving(true);
    setError("");
    try {
      await updateDoc(doc(db, "companies", companyId, "restock_requests", editTarget.id), {
        items:      validItems,
        note:       editNote.trim(),
        updated_at: serverTimestamp(),
      });
      setRequests(prev => prev.map(r =>
        r.id === editTarget.id ? { ...r, items: validItems, note: editNote.trim() } : r
      ));
      setSuccess("Request updated.");
      setEditModal(false);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Error: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setSaving(false);
  }

  async function handleApprove(req) {
    const items = getReqItems(req).filter(it => parseInt(it.requested_qty, 10) > 0);
    if (items.length === 0) return setError("Request has no valid items.");
    setApprovingId(req.id);
    setError("");
    try {
      const batch  = writeBatch(db);
      const reqRef = doc(db, "companies", companyId, "restock_requests", req.id);

      // Validate all items first, then build batch
      for (const item of items) {
        const qty         = parseInt(item.requested_qty, 10);
        const catalogRef  = doc(db, "companies", companyId, "stock_catalog", item.item_id);
        const catalogSnap = await getDoc(catalogRef);
        if (!catalogSnap.exists()) {
          setError(`Product "${item.item_name}" not found in catalog.`);
          setApprovingId(null);
          return;
        }
        const warehouseQty = catalogSnap.data().quantity || 0;
        if (qty > warehouseQty) {
          setError(`Not enough stock for "${item.item_name}". Available: ${warehouseQty}.`);
          setApprovingId(null);
          return;
        }
        const stockRef = doc(db, "companies", companyId, "vendor_stock", req.vendor_id, "items", item.item_id);
        const movRef   = doc(collection(db, "companies", companyId, "stock_movements"));
        // Deduct from warehouse
        batch.update(catalogRef, { quantity: increment(-qty), updated_at: serverTimestamp() });
        // Add to vendor stock
        batch.set(stockRef, {
          name:                item.item_name,
          unit:                item.item_unit || "",
          quantity:            increment(qty),
          low_stock_threshold: catalogSnap.data().low_stock_threshold || 0,
          updated_at:          serverTimestamp(),
        }, { merge: true });
        // Log movement
        batch.set(movRef, {
          type:         "restock",
          vendor_id:    req.vendor_id,
          vendor_name:  req.vendor_name,
          product_id:   item.item_id,
          product_name: item.item_name,
          quantity:     qty,
          note:         `Approved restock request. ${req.note || ""}`.trim(),
          created_at:   serverTimestamp(),
        });
      }
      // Mark request approved
      batch.update(reqRef, { status: "approved", approved_at: serverTimestamp() });
      await batch.commit();

      setRequests(prev => prev.map(r =>
        r.id === req.id ? { ...r, status: "approved" } : r
      ));
      setSuccess(`Approved restock for ${req.vendor_name} — ${items.length} product${items.length !== 1 ? "s" : ""} restocked`);
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setError("Error: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setApprovingId(null);
  }

  async function handleReject(req) {
    setRejectingId(req.id);
    setError("");
    try {
      await updateDoc(doc(db, "companies", companyId, "restock_requests", req.id), {
        status:      "rejected",
        rejected_at: serverTimestamp(),
      });
      setRequests(prev => prev.map(r =>
        r.id === req.id ? { ...r, status: "rejected" } : r
      ));
    } catch (err) {
      setError("Error: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setRejectingId(null);
  }

  // ── PDF print — same style as ReportsPage ─────────────────────────────────
  async function printRequestPDF(req) {
    const pdfdoc = new jsPDF();
    const now    = new Date().toLocaleString();

    function hexToRgb(hex) {
      const h = hex.replace("#", "");
      if (h.length !== 6) return [26, 86, 160];
      return [
        parseInt(h.substring(0, 2), 16),
        parseInt(h.substring(2, 4), 16),
        parseInt(h.substring(4, 6), 16),
      ];
    }
    function lightenRgb([r, g, b], amount = 0.88) {
      return [
        Math.round(r + (255 - r) * amount),
        Math.round(g + (255 - g) * amount),
        Math.round(b + (255 - b) * amount),
      ];
    }
    const brandRgb = hexToRgb(primaryColor);
    const lightRgb = lightenRgb(brandRgb);

    // ── Header bar ────────────────────────────────────────────────────────────
    pdfdoc.setFillColor(...brandRgb);
    pdfdoc.rect(0, 0, 210, 30, "F");

    // Accent strip
    pdfdoc.setFillColor(...lightenRgb(brandRgb, 0.3));
    pdfdoc.rect(0, 0, 210, 3, "F");

    pdfdoc.setTextColor(255, 255, 255);
    pdfdoc.setFontSize(18);
    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("PINPOINT", 14, 16);

    pdfdoc.setFontSize(10);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text("Restock Request Form", 14, 24);

    // Company logo
    if (companyLogo) {
      try {
        const base64 = await new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width  = img.naturalWidth  || img.width;
              canvas.height = img.naturalHeight || img.height;
              canvas.getContext("2d").drawImage(img, 0, 0);
              resolve(canvas.toDataURL("image/png"));
            } catch (_) { resolve(null); }
          };
          img.onerror = () => resolve(null);
          img.src = companyLogo;
        });
        if (base64) pdfdoc.addImage(base64, "PNG", 175, 5, 22, 22);
      } catch (_) {}
    }

    // ── Company name ──────────────────────────────────────────────────────────
    let y = 38;
    if (companyName) {
      pdfdoc.setTextColor(...brandRgb);
      pdfdoc.setFontSize(13);
      pdfdoc.setFont("helvetica", "bold");
      pdfdoc.text(companyName, 14, y);
      y += 7;
    }

    // Divider
    pdfdoc.setDrawColor(...brandRgb);
    pdfdoc.setLineWidth(0.5);
    pdfdoc.line(14, y, 196, y);
    y += 5;

    // ── Meta info ─────────────────────────────────────────────────────────────
    pdfdoc.setTextColor(60, 60, 60);
    pdfdoc.setFontSize(8.5);
    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Generated:", 14, y);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(now, 40, y);

    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Request ID:", 110, y);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(req.id.slice(-8).toUpperCase(), 132, y);
    y += 6;

    const reqDate = req.created_at?.toDate ? req.created_at.toDate().toLocaleString() : "—";
    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Date Requested:", 14, y);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(reqDate, 52, y);
    y += 8;

    // Second divider
    pdfdoc.setDrawColor(220, 220, 220);
    pdfdoc.setLineWidth(0.3);
    pdfdoc.line(14, y, 196, y);
    y += 4;

    // ── Request details table (vendor info) ───────────────────────────────────
    autoTable(pdfdoc, {
      startY: y,
      head: [["Field", "Details"]],
      body: [
        ["Vendor Name",    req.vendor_name  || "—"],
        ["Vendor Phone",   req.vendor_phone || "—"],
        ["Vendor Note",    req.note         || "—"],
        ["Status",         (req.status || "pending").toUpperCase()],
      ],
      headStyles:         { fillColor: brandRgb, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
      alternateRowStyles: { fillColor: lightRgb },
      styles:             { fontSize: 9, cellPadding: 4 },
      columnStyles:       { 0: { fontStyle: "bold", cellWidth: 55 } },
      margin:             { left: 14, right: 14 },
    });

    y = pdfdoc.lastAutoTable.finalY + 8;

    // ── Items table ────────────────────────────────────────────────────────────
    const reqItemRows = getReqItems(req).map(it => [
      it.item_name    || "—",
      it.item_unit    || "—",
      String(it.requested_qty || 0),
    ]);
    autoTable(pdfdoc, {
      startY: y,
      head: [["Product", "Unit", "Qty Requested"]],
      body:  reqItemRows.length > 0 ? reqItemRows : [["No items", "", ""]],
      headStyles:         { fillColor: brandRgb, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
      alternateRowStyles: { fillColor: lightRgb },
      styles:             { fontSize: 9, cellPadding: 4 },
      columnStyles:       { 0: { cellWidth: 95 }, 1: { cellWidth: 35 }, 2: { halign: "center" } },
      margin:             { left: 14, right: 14 },
    });

    y = pdfdoc.lastAutoTable.finalY + 16;

    // ── Signature section ──────────────────────────────────────────────────────
    pdfdoc.setDrawColor(...brandRgb);
    pdfdoc.setLineWidth(0.5);
    pdfdoc.line(14, y, 196, y);
    y += 6;

    pdfdoc.setTextColor(...brandRgb);
    pdfdoc.setFontSize(10);
    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Confirmation & Signature", 14, y);
    y += 8;

    pdfdoc.setTextColor(60, 60, 60);
    pdfdoc.setFontSize(9);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(
      `I, ${req.vendor_name || "the vendor"}, confirm receipt of the above restocked quantity and agree to the terms.`,
      14, y, { maxWidth: 182 }
    );
    y += 16;

    // Signature boxes
    pdfdoc.setDrawColor(150, 150, 150);
    pdfdoc.setLineWidth(0.3);

    // Vendor signature
    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.setFontSize(8.5);
    pdfdoc.setTextColor(80, 80, 80);
    pdfdoc.text("Vendor Signature:", 14, y);
    pdfdoc.line(14, y + 12, 90, y + 12);
    pdfdoc.setFont("helvetica", "normal");
    pdfdoc.text(req.vendor_name || "", 14, y + 18);

    // Admin signature
    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Admin / Authorized By:", 110, y);
    pdfdoc.line(110, y + 12, 196, y + 12);

    y += 24;
    // Date line
    pdfdoc.setFont("helvetica", "bold");
    pdfdoc.text("Date:", 14, y);
    pdfdoc.line(26, y, 90, y);

    pdfdoc.text("Date:", 110, y);
    pdfdoc.line(122, y, 196, y);

    // ── Footer on every page ───────────────────────────────────────────────────
    const pageCount = pdfdoc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdfdoc.setPage(i);
      pdfdoc.setFillColor(...brandRgb);
      pdfdoc.rect(0, 286, 210, 11, "F");
      pdfdoc.setTextColor(255, 255, 255);
      pdfdoc.setFontSize(7.5);
      pdfdoc.setFont("helvetica", "bold");
      pdfdoc.text("PINPOINT", 14, 293);
      if (companyName) {
        pdfdoc.setFont("helvetica", "normal");
        pdfdoc.text(`· ${companyName}`, 36, 293);
      }
      pdfdoc.text(`Page ${i} of ${pageCount}`, 196, 293, { align: "right" });
    }

    pdfdoc.save(`restock-request-${req.id.slice(-8)}-${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  const statusColors = {
    pending:  "bg-yellow-100 text-yellow-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
  };
  const statusIcons = {
    pending:  Clock,
    approved: CheckCircle,
    rejected: XCircle,
  };

  const filtered = statusFilter === "all"
    ? requests
    : requests.filter(r => r.status === statusFilter);

  const pendingCount = requests.filter(r => r.status === "pending").length;

  return (
    <div className="space-y-4">

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-indigo-600" />
            Restock Requests
            {pendingCount > 0 && (
              <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {pendingCount} pending
              </span>
            )}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {["all","pending","approved","rejected"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                statusFilter === s
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}>
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button onClick={loadAll} disabled={loading}
            className="flex items-center gap-1.5 text-sm text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <Flash msg={success} isError={false} />
      {error && !editModal && <Flash msg={error} isError />}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="h-14 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <ClipboardList className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-400 text-sm">
              {statusFilter === "pending" ? "No pending restock requests" : "No requests found"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Date</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Vendor</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Items</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500">Note</th>
                  <th className="py-3 px-4 text-center text-xs font-semibold text-gray-500">Status</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(req => {
                  const StatusIcon = statusIcons[req.status] || Clock;
                  const reqItems   = getReqItems(req);
                  const isExpanded = expanded === req.id;
                  return (
                    <React.Fragment key={req.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-500 text-xs whitespace-nowrap">
                          {formatTs(req.created_at)}
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm font-semibold text-gray-800">{req.vendor_name || "—"}</p>
                          {req.vendor_phone && (
                            <p className="text-xs text-gray-400">{req.vendor_phone}</p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {reqItems.slice(0, 2).map((it, i) => (
                              <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
                                {it.item_name} ×{it.requested_qty}
                              </span>
                            ))}
                            {reqItems.length > 2 && (
                              <span className="text-xs text-gray-400">+{reqItems.length - 2} more</span>
                            )}
                          </div>
                          {reqItems.length > 1 && (
                            <button
                              onClick={() => setExpanded(isExpanded ? null : req.id)}
                              className="text-[10px] text-indigo-500 hover:underline mt-0.5">
                              {isExpanded ? "Hide details" : `View all ${reqItems.length} products`}
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-xs max-w-[160px] truncate">
                          {req.note || "—"}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold ${statusColors[req.status] || "bg-gray-100 text-gray-600"}`}>
                            <StatusIcon className="w-3 h-3" />
                            {(req.status || "pending").charAt(0).toUpperCase() + (req.status || "pending").slice(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-1.5 flex-wrap">
                            {/* Print PDF */}
                            <button onClick={() => printRequestPDF(req)}
                              className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 bg-gray-50 hover:bg-gray-100 px-2.5 py-1.5 rounded-lg transition">
                              <Printer className="w-3.5 h-3.5" /> PDF
                            </button>
                            {/* Edit */}
                            {req.status === "pending" && (
                              <button onClick={() => openEdit(req)}
                                className="flex items-center gap-1 text-xs text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-lg transition">
                                <Pencil className="w-3.5 h-3.5" /> Edit
                              </button>
                            )}
                            {/* Approve */}
                            {req.status === "pending" && (
                              <button
                                onClick={() => handleApprove(req)}
                                disabled={approvingId === req.id}
                                className="flex items-center gap-1 text-xs text-green-700 border border-green-200 bg-green-50 hover:bg-green-100 px-2.5 py-1.5 rounded-lg transition disabled:opacity-60">
                                {approvingId === req.id
                                  ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                  : <CheckCircle className="w-3.5 h-3.5" />}
                                Approve
                              </button>
                            )}
                            {/* Reject */}
                            {req.status === "pending" && (
                              <button
                                onClick={() => handleReject(req)}
                                disabled={rejectingId === req.id}
                                className="flex items-center gap-1 text-xs text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 px-2.5 py-1.5 rounded-lg transition disabled:opacity-60">
                                {rejectingId === req.id
                                  ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                  : <XCircle className="w-3.5 h-3.5" />}
                                Reject
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Expanded items detail row */}
                      {isExpanded && (
                        <tr className="bg-indigo-50">
                          <td colSpan={6} className="px-6 py-3">
                            <p className="text-xs font-semibold text-gray-500 mb-2">
                              Products in this request ({reqItems.length})
                            </p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left py-1">Product</th>
                                  <th className="text-left py-1">Unit</th>
                                  <th className="text-center py-1">Qty Requested</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reqItems.map((it, i) => (
                                  <tr key={i} className="border-t border-indigo-100">
                                    <td className="py-1 font-medium text-gray-800">{it.item_name}</td>
                                    <td className="py-1 text-gray-600">{it.item_unit || "—"}</td>
                                    <td className="py-1 text-center font-bold text-indigo-700">{it.requested_qty}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal && editTarget && (
        <Modal isOpen={true} onClose={() => setEditModal(false)} title="Edit Restock Request" size="sm">
          <div className="space-y-4">
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-sm text-gray-700">
              <p>Vendor: <strong>{editTarget.vendor_name}</strong></p>
            </div>
            {error && <Flash msg={error} isError />}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Items &amp; Quantities <span className="text-red-400">*</span>
              </label>
              <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                {editItems.map((item, i) => (
                  <div key={item.item_id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{item.item_name}</p>
                      <p className="text-xs text-gray-400">{item.item_unit}</p>
                    </div>
                    <input
                      type="number" min="0"
                      value={item.requested_qty}
                      onChange={e => {
                        const updated = [...editItems];
                        updated[i] = { ...updated[i], requested_qty: e.target.value };
                        setEditItems(updated);
                      }}
                      placeholder="Qty"
                      className="w-20 border border-gray-200 rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 shrink-0" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Set qty to 0 to remove an item from this request.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
              <textarea value={editNote}
                onChange={e => setEditNote(e.target.value)}
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                placeholder="Add or update note..." />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditModal(false)} disabled={saving}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-3 rounded-xl hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleSaveEdit} disabled={saving}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-bold py-3 rounded-xl transition">
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default StockManager;
